import { notificationsActionCreators } from 'src/actions/notifications/actionCreators';
import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { collaborationSlice } from 'src/store/collaboration/collaboration.slice';
import { getStore } from 'src/store/store';
import * as collaborationProvider from 'src/utils/collaboration/collaborationProvider';
import { yDocToProject } from 'src/utils/collaboration/scbf/yDocToProject';

// Propagate collaboration auth state from the provider into Redux. These
// listeners are registered once and survive reconnects, so they live at module
// scope behind an idempotent guard rather than being attached per connection.
let collaborationAuthListenersRegistered = false;
export const registerCollaborationAuthListeners = (): void => {
  if (collaborationAuthListenersRegistered) {
    return;
  }
  collaborationAuthListenersRegistered = true;

  collaborationProvider.addReadOnlyChangeEventListener(
    (projectId, readOnly) => {
      getStore().dispatch(
        collaborationSlice.actions.setCollaborationReadOnly({
          projectId,
          readOnly,
        })
      );
    }
  );

  collaborationProvider.addAuthFailureEventListener((projectId) => {
    const store = getStore();
    store.dispatch(
      collaborationSlice.actions.clearedCollaborationProject({ projectId })
    );
    store.dispatch(notificationsActionCreators.collaborationAuthFailed());
  });
};

/**
 * Open a collaboration connection for a project and bridge it into Redux.
 *
 * Remote Y.Doc updates are dispatched as RECEIVED_COLLABORATION_UPDATE. The
 * project is marked read-only synchronously right after connecting (fail
 * closed): the server's `Authenticated` message later reports the real role
 * via the read-only listener, and an auth failure clears the entry, restoring
 * the legacy editable path. A connection failure is swallowed so the legacy
 * save path can take over.
 */
export const connectCollaborationForProject = (projectId: string): void => {
  registerCollaborationAuthListeners();
  try {
    collaborationProvider.connect({
      projectId,
      onRemoteUpdate: (doc) => {
        const project = yDocToProject(doc);
        getStore().dispatch(
          projectsActionCreators.receivedCollaborationUpdate(projectId, project)
        );
      },
    });
    getStore().dispatch(
      collaborationSlice.actions.setCollaborationReadOnly({
        projectId,
        readOnly: true,
      })
    );
  } catch (err) {
    // Connection failure should not block project loading —
    // the legacy save path will be used as fallback.
    console.warn('Collaboration connection failed, using legacy save', err);
  }
};

/**
 * Close a project's collaboration connection and clear its read-only state.
 * Safe to call when no connection exists.
 */
export const disconnectCollaborationForProject = (projectId: string): void => {
  collaborationProvider.disconnect(projectId);
  getStore().dispatch(
    collaborationSlice.actions.clearedCollaborationProject({ projectId })
  );
};
